import { Component, OnInit } from '@angular/core';
import {
  DeviceDetailSummaryComponent,
  DeviceInfoExportHistory,
  BatteryHistoryViewModel,
  ChartPointViewModel,
  DeviceSettingViewModel,
  SensingType,
} from './device-detail-summary/device-detail-summary.component';
import {
  DeviceDetailMapComponent,
  DeviceLocationViewModel,
} from './device-detail-map/device-detail-map.component';
import { DeviceInfoService } from '@services/api/device-info.service';
import { DeviceInfoViewModel } from '@components/device-info/device-detail/device-detail-summary/device-detail-summary.component';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { DeviceSettingService } from '@services/api/device-setting.service';
import {
  DeviceDetailChartComponent,
  SensingModel,
} from './device-detail-chart/device-detail-chart.component';
import { LoadingService } from '@services/loading.service';
import { format, subDays } from 'date-fns';
import { UTCDate } from '@date-fns/utc';
import { DeviceSettingResponse } from '@entities/deviceSetting';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CustomValidator } from '@utils/custom-validators';
import { ExportService } from '@services/export.service';
import { DeviceInfoDetailCsv } from '@entities/deviceInfo';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { LogService } from '@services/api/log.service';
import { AccountResponse } from '@entities/account';
import { BATTERY_LOW_THRESHOLD_PERCENT } from './device-detail.constants';

@Component({
  selector: 'app-device-detail',
  standalone: true,
  imports: [
    RouterModule,
    MatIconModule,
    MatButtonModule,
    DeviceDetailSummaryComponent,
    DeviceDetailMapComponent,
    DeviceDetailChartComponent,
  ],
  templateUrl: './device-detail.component.html',
  styleUrl: './device-detail.component.scss',
})
export class DeviceDetailComponent implements OnInit {
  /** デバイス名 */
  public deviceName: string = '';

  /** バッテリー */
  public batteryPercentage: number | null = null;

  /** 最終更新日時 */
  public lastCommunicationAt: string = '';

  /** フライトモードのON/OFF */
  public fmdOn?: boolean | null = null;

  /** フライトモードの有効/無効 */
  public fmdEnabled?: boolean | null = null;

  /** 選択された計測種別 */
  public selectedSensingType: SensingType | '' | undefined;

  /** デバイスID */
  private deviceId: string = '';

  /** デバイス最新情報 */
  public latestDeviceInfo: DeviceInfoViewModel = {};

  /** デバイス履歴情報 */
  public deviceInfoExportHistory: DeviceInfoExportHistory[] = [];

  /** デバイス位置情報 */
  public deviceLocationList: DeviceLocationViewModel[] = [];

  /** 電源グラフ用のバッテリー履歴 */
  public batteryHistoryList: BatteryHistoryViewModel[] = [];

  /** 検索期間内の最新バッテリー */
  public historyBatteryPercentage: number | null = null;

  /** 電源表示用のLocation履歴 */
  public batteryLocationList: DeviceLocationViewModel[] = [];

  /** 検索期間内の最新バッテリー日時 */
  public historyBatteryReportedAt: Date | null = null;

  /** 検索期間内に低下したバッテリーがあるか */
  public batteryLow = false;

  /** デバイス設定 */
  public deviceSetting: DeviceSettingViewModel = {};

  /** デバイスのセンサー閾値情報 */
  public deviceSensorThresholdInfo: SensingModel = {};

  /** 検索フォーム */
  public historyFormGroup: FormGroup;

  /** 選択されたチャートポイント */
  public selectedChartPoints: ChartPointViewModel | undefined;

  /** テナント作成日時 */
  public tenantCreatedAt: string | null = '';

  /** 検索ボタン押下時の最終検索日時（CSVエクスポート時の日時指定に使用） */
  private lastSearchDatetime: {
    historyFrom: string;
    historyTo: string;
  } = { historyFrom: '', historyTo: '' };

  /** CSVのヘッダーを作成するためのマップ */
  private readonly csvColumnMap = [
    { key: 'serverReceivedTimeStamp', label: 'serverReceivedTimeStamp' },
    { key: 'deviceReportingTimeStamp', label: 'deviceReportingTimeStamp' },
    { key: 'deviceName', label: 'Device Name' },
    { key: 'serialNumber', label: 'Serial Number' },
    { key: 'status', label: 'Operational Status' },
    { key: 'alertTimeStamp', label: 'TimeStamp' },
    { key: 'temperature', label: 'Temperature' },
    { key: 'temperatureAlertType', label: 'Temperature Alert Type' },
    { key: 'pressure', label: 'Pressure' },
    { key: 'pressureAlertType', label: 'Pressure Alert Type' },
    { key: 'humidity', label: 'Humidity' },
    { key: 'humidityAlertType', label: 'Humidity Alert Type' },
    { key: 'light', label: 'Light' },
    { key: 'lightAlertType', label: 'Light Alert Type' },
    { key: 'tilt', label: 'Tilt' },
    { key: 'tiltAlertType', label: 'Tilt Alert Type' },
    { key: 'fallDetectedEventType', label: 'Fall Detection' },
    { key: 'mcdDetectedEventType', label: 'Motion Detection' },
    { key: 'lightExposureEventType', label: 'Light Exposure' },
    { key: 'shockDetectedEventType', label: 'Shock Detection' },
    { key: 'batteryPercentage', label: 'Battery %' },
    { key: 'latitude', label: 'Latitude' },
    { key: 'longitude', label: 'Longitude' },
    { key: 'locationTechType', label: 'Reported Location Technology' },
    { key: 'address', label: 'Address' },
  ];

  /** エクスポート用のCSVデータモデル */
  public deviceInfoDetailCsv: DeviceInfoDetailCsv[] = [];

  constructor(
    private deviceInfoService: DeviceInfoService,
    private deviceSettingService: DeviceSettingService,
    private exportService: ExportService,
    private route: ActivatedRoute,
    private loadingService: LoadingService,
    private logService: LogService,
    private dialog: MatDialog,
  ) {
    const tenant = route.snapshot.data['loggedInUser'].tenant;
    this.tenantCreatedAt = tenant.createdAt;
    const now = new Date();
    this.historyFormGroup = new FormGroup(
      {
        historyFrom: new FormControl(subDays(now, 1), {
          validators: [
            Validators.required,
            CustomValidator.earlyDateValidator(2),
            CustomValidator.NotBeforeCreationDateValidator(
              this.tenantCreatedAt,
            ),
          ],
        }),
        historyTo: new FormControl(now, {
          validators: [Validators.required],
        }),
      },
      {
        validators: CustomValidator.startDateTimeBeforeEndDatetimeValidator(
          'historyFrom',
          'historyTo',
        ),
      },
    );
  }

  ngOnInit() {
    this.route.params.subscribe((params) => {
      this.deviceId = params['deviceId'];

      this.route.queryParams.subscribe((queryParams) => {
        const historyFrom: string | undefined = queryParams['historyFrom'];
        if (historyFrom) {
          this.historyFormGroup
            .get('historyFrom')
            ?.setValue(new Date(historyFrom));
        }
        const historyToDateString: string | undefined =
          queryParams['historyTo'];
        if (historyToDateString) {
          this.historyFormGroup
            .get('historyTo')
            ?.setValue(new Date(historyToDateString));
        }

        this._initLatestDeviceInfo();
      });
    });

    // マクニカ管理者であれば、2ヵ月以上前でも指定可能とする
    const loggedInUser: AccountResponse =
      this.route.snapshot.data['loggedInUser'];
    if (loggedInUser.tenant.roleId == 'system_admin') {
      this.historyFormGroup.controls['historyFrom'].setValidators([
        Validators.required,
        CustomValidator.NotBeforeCreationDateValidator(this.tenantCreatedAt),
      ]);
    }
  }

  private _initLatestDeviceInfo() {
    // デバイス設定情報を取得
    this.deviceSettingService.find(this.deviceId).subscribe({
      next: (tracker) => {
        const deviceType = tracker.deviceType;
        this.deviceSetting = {
          deviceName: tracker.deviceName,
        };

        // 各センサーの設定を取得
        this.deviceSetting.temperature = {
          availability: deviceType.temperatureSensor || false,
          enabled: tracker.temperature,
        };
        this.deviceSetting.humidity = {
          availability: deviceType.humiditySensor || false,
          enabled: tracker.humidity,
        };
        this.deviceSetting.pressure = {
          availability: deviceType.pressureSensor || false,
          enabled: tracker.pressure,
        };
        this.deviceSetting.light = {
          availability: deviceType.lightSensor || false,
          enabled: tracker.light,
        };
        this.deviceSetting.tilt = {
          availability: deviceType.tiltSensor || false,
          enabled: tracker.tilt,
        };
        this.deviceSetting.fallDetected = {
          availability: deviceType.fallDetected || false,
          enabled: tracker.fallDetected,
        };
        this.deviceSetting.shockDetected = {
          availability: deviceType.shockDetected || false,
          enabled: tracker.shockDetected,
        };
        this.deviceSetting.mcdDetected = {
          availability: deviceType.mcdDetected || false,
          enabled: tracker.mcdDetected,
        };
        this.deviceSetting.lightExposure = {
          availability: deviceType.lightExposureDetected || false,
          enabled: tracker.lightExposureDetected,
        };

        /** センサーの閾値情報を取得 */
        this.setSensorThreshold(tracker);
      },
    });

    const historyFrom = this.historyFormGroup.value.historyFrom.toISOString();
    const historyTo = this.historyFormGroup.value.historyTo.toISOString();

    // デバイス情報を取得
    this.deviceInfoService
      .findByIdLatest(this.deviceId, historyFrom, historyTo)
      .subscribe({
        next: (latestDeviceInfo) => {
          this.deviceName =
            latestDeviceInfo.deviceName || latestDeviceInfo.deviceId;
          this.batteryPercentage = latestDeviceInfo.batteryPercentage ?? null;
          this.lastCommunicationAt = latestDeviceInfo.lastCommunicationAt;
          this.fmdOn = latestDeviceInfo.fmdOn;
          this.fmdEnabled = latestDeviceInfo.fmdEnabled;
        },
      });

    // 初期表示時の対象期間で履歴を取得する
    this.findbyIdHistory();
  }

  /**
   * 対象期間の履歴情報と履歴内の最新状態を取得する
   */
  public findbyIdHistory() {
    const historyFrom = this.historyFormGroup.value.historyFrom.toISOString();
    const historyTo = this.historyFormGroup.value.historyTo.toISOString();

    this.lastSearchDatetime.historyFrom = historyFrom;
    this.lastSearchDatetime.historyTo = historyTo;

    this.deviceInfoService
      .findByIdHistoryLatest(this.deviceId, historyFrom, historyTo)
      .subscribe({
        next: (latestDeviceInfoHistory) => {
          this.latestDeviceInfo = latestDeviceInfoHistory;
        },
      });

    this.deviceInfoService
      .findByIdHistory(this.deviceId, historyFrom, historyTo)
      .subscribe({
        next: (historyList) => {
          this.deviceLocationList = historyList;
          this.updateBatteryAndLocationHistory(
            historyList,
            new Date(historyFrom).getTime(),
            new Date(historyTo).getTime(),
          );
          this.selectedChartPoints = undefined;
        },
      });
  }

  public sensingTypeChanged(sensor: SensingType | '') {
    this.selectedChartPoints = undefined;
    this.selectedSensingType = sensor;
  }

  public findbyIdExportHistory() {
    this.deviceInfoService
      .findByIdExportHistory(
        this.deviceId,
        this.lastSearchDatetime.historyFrom,
        this.lastSearchDatetime.historyTo,
      )
      .subscribe({
        next: (deviceInfoExportHistory) => {
          this.deviceInfoExportHistory = deviceInfoExportHistory;
          this.exportToCsv(
            this.lastSearchDatetime.historyFrom,
            this.lastSearchDatetime.historyTo,
            this.deviceInfoExportHistory,
            this.deviceName,
          );
        },
      });

    const params: { [key: string]: string } = {
      serial_number: this.deviceId,
      history_from: this.lastSearchDatetime.historyFrom,
      history_to: this.lastSearchDatetime.historyTo,
    };
    // ログの登録自体は失敗しても処理を継続する。
    this.logService.add('export_device_info', params).subscribe({
      error: () => {
        // ログ埋め込み処理が失敗した場合でも処理を止めない。
      },
    });
  }
  /**
   * CSVデータをエクスポートし、ダウンロードを開始する
   * @param historyFrom CSVのカラムマップ
   * @param historyTo CSVに変換するデータのリスト
   * @param deviceInfoExportHistory エクスポート用に取得したデータの
   * @param deviceName 日付のフォーマット
   */
  public async exportToCsv(
    historyFrom: string,
    historyTo: string,
    deviceInfoExportHistory: DeviceInfoExportHistory[],
    deviceName: string,
  ) {
    this.loadingService.show();
    try {
      this.deviceInfoDetailCsv = deviceInfoExportHistory.map((info) => ({
        serverReceivedTimeStamp: this.formatEpochToUTC(
          info.serverReceivedEpochtime,
        ),
        deviceReportingTimeStamp: this.formatEpochToUTC(
          info.deviceReportedEpochtime,
        ),
        temperature: info.temperature ?? null,
        temperatureAlertType: info.temperatureEventType ?? '',
        humidity: info.humidity ?? null,
        humidityAlertType: info.humidityEventType ?? '',
        pressure: info.pressure ?? null,
        pressureAlertType: info.pressureEventType ?? '',
        light: info.light ?? null,
        lightAlertType: info.lightEventType ?? '',
        tilt: info.tilt ?? null,
        tiltAlertType: info.tiltEventType ?? '',
        latitude: info.latitude ?? null,
        longitude: info.longitude ?? null,
        locationTechType: info.locationTechType ?? '',
        address: info.address ?? '',
        batteryPercentage: info.batteryPercentage ?? '',
        status: info.status ?? '',
        fallDetectedEventType: info.fallDetectedEventType ?? '',
        mcdDetectedEventType: info.mcdDetectedEventType ?? '',
        lightExposureEventType: info.lightExposureEventType ?? '',
        shockDetectedEventType: info.shockDetectedEventType ?? '',
        alertTimeStamp: this.formatEpochToUTC(info.alertTimeStamp),
        serialNumber: info.serialNumber ?? '',
        deviceName: deviceName,
      }));

      const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return format(date, 'yyyyMMddHHmm');
      };

      const startDate = formatDate(historyFrom);
      const endDate = formatDate(historyTo);
      const fileName = `${startDate}-${endDate}_${deviceName}.csv`;

      this.exportService.exportToCsv(
        this.csvColumnMap,
        this.deviceInfoDetailCsv,
        fileName,
        true,
        "yyyy-MM-dd'T'HH:mm:ss",
        'Asia/Tokyo',
      );
    } catch (error) {
      const alertDialogOptions: AlertDialogOption = {
        type: 'error',
        message: `CSVのエクスポートに失敗しました。\nしばらくしてからもう一度お試しください。`,
      };
      this.dialog.open(AlertDialogComponent, {
        width: '600px',
        data: alertDialogOptions,
      });
    } finally {
      this.loadingService.hide();
    }
  }

  /**
   * 選択されてたチャートポイントが変更された時
   * @param chartPoints チャートポイント
   */
  public onSelectedChartPoints(chartPoints: ChartPointViewModel) {
    // 同じ点の再選択も子コンポーネントの入力変更として検知させる
    this.selectedChartPoints = { ...chartPoints };
  }

  private updateBatteryAndLocationHistory(
    historyList: DeviceLocationViewModel[],
    historyFrom: number,
    historyTo: number,
  ): void {
    const batteryKeys = new Set<string>();
    this.batteryHistoryList = historyList
      .flatMap((history) => {
        const batteryPercentage = history.batteryPercentage;
        const deviceReportedEpochtime = history.deviceReportedEpochtime;
        if (
          batteryPercentage == null ||
          deviceReportedEpochtime == null ||
          deviceReportedEpochtime < historyFrom ||
          deviceReportedEpochtime > historyTo
        ) {
          return [];
        }
        const key = `${deviceReportedEpochtime}:${batteryPercentage}`;
        if (batteryKeys.has(key)) {
          return [];
        }
        batteryKeys.add(key);
        return [{ batteryPercentage, deviceReportedEpochtime }];
      })
      .sort(
        (left, right) =>
          left.deviceReportedEpochtime - right.deviceReportedEpochtime,
      );

    const latestBattery = this.batteryHistoryList.at(-1);
    this.historyBatteryPercentage = latestBattery?.batteryPercentage ?? null;
    this.historyBatteryReportedAt = latestBattery
      ? new Date(latestBattery.deviceReportedEpochtime)
      : null;
    this.batteryLow = this.batteryHistoryList.some(
      (battery) => battery.batteryPercentage < BATTERY_LOW_THRESHOLD_PERCENT,
    );

    const locationKeys = new Set<string>();
    this.batteryLocationList = historyList
      .filter((history) => {
        const timeStamp = history.location?.timeStamp;
        return (
          timeStamp == null ||
          (timeStamp >= historyFrom && timeStamp <= historyTo)
        );
      })
      .filter((history) => {
        const location = history.location;
        if (location?.latitude == null || location.longitude == null) {
          return false;
        }
        const key = `${location.timeStamp ?? 'missing'}:${location.latitude}:${location.longitude}`;
        if (locationKeys.has(key)) {
          return false;
        }
        locationKeys.add(key);
        return true;
      })
      .sort((left, right) => {
        const leftTime = left.location?.timeStamp;
        const rightTime = right.location?.timeStamp;
        if (leftTime == null) return 1;
        if (rightTime == null) return -1;
        return leftTime - rightTime;
      });
  }

  private setSensorThreshold(tracker: DeviceSettingResponse) {
    if (tracker.temperature) {
      this.deviceSensorThresholdInfo.temperature = {
        AlertMax: tracker.temperatureAlertMax,
        AlertMin: tracker.temperatureAlertMin,
        PrealertMax: tracker.temperaturePrealertMax,
        PrealertMin: tracker.temperaturePrealertMin,
      };
    }

    if (tracker.humidity) {
      this.deviceSensorThresholdInfo.humidity = {
        AlertMax: tracker.humidityAlertMax,
        AlertMin: tracker.humidityAlertMin,
        PrealertMax: tracker.humidityPrealertMax,
        PrealertMin: tracker.humidityPrealertMin,
      };
    }

    if (tracker.pressure) {
      this.deviceSensorThresholdInfo.pressure = {
        AlertMax: tracker.pressureAlertMax,
        AlertMin: tracker.pressureAlertMin,
        PrealertMax: tracker.pressurePrealertMax,
        PrealertMin: tracker.pressurePrealertMin,
      };
    }

    if (tracker.tilt) {
      this.deviceSensorThresholdInfo.tilt = {
        AlertMax: tracker.tiltAlertMax,
        AlertMin: tracker.tiltAlertMin,
        PrealertMax: tracker.tiltPrealertMax,
        PrealertMin: tracker.tiltPrealertMin,
      };
    }

    if (tracker.light) {
      this.deviceSensorThresholdInfo.light = {
        AlertMax: tracker.lightAlertMax,
        AlertMin: tracker.lightAlertMin,
        PrealertMax: tracker.lightPrealertMax,
        PrealertMin: tracker.lightPrealertMin,
      };
    }

    if (tracker.shockDetected) {
      this.deviceSensorThresholdInfo.shockDetected =
        tracker.shockDetectedDetectValue;
    }
  }

  private formatEpochToUTC = (epochTime: number) => {
    if (epochTime === null || epochTime === undefined) {
      return '';
    }
    const utcDate = new UTCDate(epochTime);
    return format(utcDate, 'yyyy-MM-dd HH:mm:ss.SSS');
  };
}
